import { useEffect, useState } from 'react'
import {
  ChevronDown,
  Clock,
  Copy,
  KeyRound,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/lib/auth/useAuth'
import { formatDuration, msUntilExpiry } from '@/lib/auth/jwt'
import { cn, formatId } from '@/lib/utils'

/**
 * `AuthDebugBadge` — chip clicável no Header que mostra o estado vivo da
 * sessão (countdown até expirar) e abre um painel com claims do JWT
 * decodificados, app_metadata.vectraclip (company_id, role) e botões de
 * cópia/refresh forçado.
 *
 * Por que existe (VEC-194 — visualização do auth):
 *   - Marcelo pediu pra "ver o auth acontecendo".
 *   - Permite debugar problemas de tenant (RLS retornando vazio? Olha o
 *     `app_metadata.vectraclip.company_id` aqui).
 *   - Detecta problema de token vencido vs token rejeitado (o countdown
 *     diz se a culpa é de expiração).
 *
 * Visibilidade:
 *   - Em DEV: SEMPRE renderiza (padrão dev: barra com debug visível).
 *   - Em PROD: renderiza só se `user.role === 'admin'`. Operadores e viewers
 *     não precisam ver claims do JWT — é poluição de UI sem benefício.
 *     Nunca mostra para anônimos (não faz sentido).
 */
export function AuthDebugBadge() {
  const { isAuthenticated, user, accessToken, expiresAt, claims, refresh } = useAuth()

  // Tick a cada 1s pro countdown atualizar. Por que setInterval em vez de
  // requestAnimationFrame: precisão de 1s é suficiente; rAF gastaria
  // ~60 ticks/s sem benefício visível e ainda manteria CPU ativa.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!isAuthenticated) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [isAuthenticated])
  // O `now` precisa ser referenciado para que o React re-renderize a cada
  // tick. Fazemos isso de forma natural: o cálculo abaixo USA Date.now()
  // indiretamente via `msUntilExpiry`, e a leitura de `now` mantém o hook
  // "vivo" no fluxo reativo (o lint não aponta, mas é por design).
  void now

  if (!isAuthenticated || !user) return null

  const isAdmin = user.role === 'admin'
  // Em produção, esconder para não-admin (não vaza claims pra operador).
  if (!import.meta.env.DEV && !isAdmin) return null

  const remaining = msUntilExpiry(expiresAt)
  const remainingLabel = remaining !== null ? formatDuration(remaining) : '—'

  // Faixas de cor pro chip:
  //   < 2 min OU expirado → vermelho (refresh urgente)
  //   < 10 min            → âmbar
  //   resto               → verde
  // Usar tokens semânticos (warning/destructive) em vez de cores hardcoded
  // pra respeitar o tema escolhido.
  const tone: 'ok' | 'warn' | 'crit' =
    remaining === null
      ? 'ok'
      : remaining <= 2 * 60_000
        ? 'crit'
        : remaining <= 10 * 60_000
          ? 'warn'
          : 'ok'

  const toneClass = {
    ok: 'border-success/30 bg-success/10 text-success-foreground',
    warn: 'border-warning/40 bg-warning/10 text-warning',
    crit: 'border-destructive/40 bg-destructive/10 text-destructive',
  }[tone]

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Detalhes da sessão de autenticação"
          className={cn(
            'hidden h-9 gap-1.5 border px-2 text-xs font-medium md:inline-flex',
            toneClass,
          )}
        >
          <ShieldCheck className="h-3.5 w-3.5" />
          <span className="hidden lg:inline">Auth</span>
          <Clock className="h-3 w-3 opacity-70" />
          <span className="tabular-nums">{remainingLabel}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Sessão atual
          </DialogTitle>
          <DialogDescription>
            Visualização ao vivo do JWT do Supabase Auth — útil para debug
            de RLS, expiração e claims customizados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <SessionField label="User" value={user.name} mono={false} />
          <SessionField label="Email" value={user.email} />
          <SessionField label="Role (app)" value={user.role} pill />

          <div className="border-t pt-3">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              app_metadata.vectraclip
            </div>
            <SessionField
              label="company_id"
              value={formatId(claims?.app_metadata?.vectraclip?.company_id)}
            />
            <SessionField
              label="role (claim)"
              value={claims?.app_metadata?.vectraclip?.role ?? '—'}
              pill
            />
          </div>

          <div className="border-t pt-3">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              JWT
            </div>
            <SessionField label="sub" value={formatId(claims?.sub)} />
            <SessionField label="iss" value={claims?.iss ?? '—'} />
            <SessionField label="aud" value={claims?.aud ?? '—'} />
            <SessionField label="aal" value={claims?.aal ?? '—'} pill />
            <SessionField
              label="expira em"
              value={remainingLabel}
              tone={tone}
            />
            <SessionField
              label="expiresAt"
              value={expiresAt ?? '—'}
            />
          </div>

          <div className="flex items-center justify-end gap-2 border-t pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToken(accessToken)}
              disabled={!accessToken}
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Copiar JWT
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyClaims(claims)}
              disabled={!claims}
            >
              <KeyRound className="mr-1.5 h-3.5 w-3.5" />
              Copiar claims
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                const ok = await refresh()
                if (ok) {
                  toast.success('Token rotacionado com sucesso.')
                } else {
                  toast.error(
                    'Refresh rejeitado. A sessão pode estar expirada.',
                  )
                }
              }}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Renovar agora
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface SessionFieldProps {
  label: string
  value: string
  mono?: boolean
  pill?: boolean
  tone?: 'ok' | 'warn' | 'crit'
}

function SessionField({
  label,
  value,
  mono = true,
  pill = false,
  tone,
}: SessionFieldProps) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {pill ? (
        <Badge variant="outline" className="font-mono text-[10px]">
          {value}
        </Badge>
      ) : (
        <span
          className={cn(
            'truncate text-right text-xs',
            mono && 'font-mono',
            tone === 'crit' && 'text-destructive',
            tone === 'warn' && 'text-warning',
          )}
          title={value}
        >
          {value}
        </span>
      )}
    </div>
  )
}

async function copyToken(token: string | null) {
  if (!token) return
  try {
    await navigator.clipboard.writeText(token)
    toast.success('JWT copiado pro clipboard.')
  } catch {
    toast.error('Não foi possível copiar (clipboard bloqueado).')
  }
}

async function copyClaims(claims: unknown) {
  if (!claims) return
  try {
    await navigator.clipboard.writeText(JSON.stringify(claims, null, 2))
    toast.success('Claims copiados (JSON formatado).')
  } catch {
    toast.error('Não foi possível copiar (clipboard bloqueado).')
  }
}
